"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import PublicLayout from "@/components/layout/PublicLayout";
import { CheckCircle2, AlertCircle, Mail, ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  useEffect(() => {
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
      const existing = document.querySelector('script[src="https://www.google.com/recaptcha/api.js"]');
      if (existing) {
        setRecaptchaReady(true);
        return;
      }
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.grecaptcha.ready(() => {
          setRecaptchaReady(true);
        });
      };
      document.head.appendChild(script);
    } else {
      setRecaptchaReady(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      let recaptchaToken = "";
      if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
        if (!window.grecaptcha) {
          setError("Security verification not ready. Please try again.");
          setLoading(false);
          return;
        }
        recaptchaToken = await window.grecaptcha.execute(siteKey, { action: "forgot_password" });
      }

      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, recaptchaToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to process request");
        setLoading(false);
        return;
      }

      setSuccess("Password reset link has been sent to your email");
      setSubmitted(true);
      setEmail("");
    } catch (error) {
      console.error("Forgot password error:", error);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="relative min-h-[85vh] bg-linear-to-br from-slate-950 via-slate-900 to-blue-950 flex items-center justify-center px-4 sm:px-6 py-12 md:py-20 overflow-hidden font-sans">
        
        {/* Ambient Glows */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none"></div>

        <div className="relative w-full max-w-md space-y-6">
          
          {/* Header Title */}
          <div className="text-center space-y-2">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300 text-xs font-bold uppercase tracking-widest">
              ACCOUNT RECOVERY
            </span>
            <h1 className="text-3xl font-black text-white tracking-tight">
              Reset Password
            </h1>
            <p className="text-xs sm:text-sm text-slate-300">
              Enter your garage email and we&apos;ll send you a secure reset link
            </p>
          </div>

          <div className="bg-white rounded-3xl p-7 sm:p-9 border-2 border-slate-200/90 shadow-2xl">
            {submitted && success ? (
              <div className="space-y-5 text-center py-2">
                <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center mx-auto">
                  <CheckCircle2 size={32} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-slate-900">Check Your Inbox</h3>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {success}. If you don&apos;t see it within a few minutes, check your spam or junk folder.
                  </p>
                </div>
                <Link
                  href="/login"
                  className="w-full bg-linear-to-r from-blue-600 to-teal-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg shadow-blue-500/30 hover:scale-[1.01] transition-all flex items-center justify-center gap-2 text-sm cursor-pointer"
                >
                  <ArrowLeft size={16} />
                  <span>Back to Login</span>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs font-medium flex items-center gap-2">
                    <AlertCircle size={16} className="shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={17} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !recaptchaReady}
                  className="w-full bg-linear-to-r from-blue-600 to-teal-600 hover:from-blue-500 hover:to-teal-500 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:scale-[1.01] transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer mt-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending Link...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Password Reset Link</span>
                      <ArrowRight size={17} />
                    </>
                  )}
                </button>

                <div className="mt-6 pt-5 border-t border-slate-100 text-center">
                  <p className="text-xs text-slate-600">
                    Remember your credentials?{" "}
                    <Link href="/login" className="text-blue-600 font-bold hover:underline">
                      Sign in here
                    </Link>
                  </p>
                </div>
              </form>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
            <ShieldCheck size={14} className="text-teal-400" />
            <span>Secure account recovery • 256-bit SSL</span>
          </div>

        </div>
      </div>
    </PublicLayout>
  );
}
